import React, { useState, useEffect } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import useApi from "storybook-dashboard/utils/fetching";
import { Hide, nestByDunderKeys } from "storybook-dashboard/utils";
import { useModal, Modal } from "storybook-dashboard/components/modal";
import UserRoleTree from "./tree";
import Im from "immutable";
import Moment from "moment";
import Octicon from "react-octicon";
import { getRoleName, getDashboardId } from "./utils";
import { getLastLoginFormatted } from "../memberList";

// Helper function to check if updates will remove all permissions
function checkIfUpdatesRemoveAllPermissions(
  updates,
  userRolesByDashboardId,
  userCompanyRoleNames,
  userProjectRoleNames
) {
  if (!updates || updates.size === 0) return false;

  // Count current permissions
  const currentPermissionCount = userCompanyRoleNames.size + userProjectRoleNames.size;

  // Count how many permissions will be removed by the updates
  let permissionsToRemove = 0;

  updates.entrySeq().forEach(([dashboardId, dashboardUpdates]) => {
    const currentRoles = userRolesByDashboardId.get(dashboardId, Im.List());

    dashboardUpdates.entrySeq().forEach(([roleKey, isEnabled]) => {
      if (!isEnabled) {
        // This role is being disabled/removed
        const hasCurrentRole = currentRoles.some((role) => {
          const roleName = getRoleName(role);
          return roleKey.includes(roleName) || roleKey === roleName;
        });

        if (hasCurrentRole) {
          permissionsToRemove++;
        }
      }
    });
  });

  // Check if all current permissions will be removed
  return permissionsToRemove >= currentPermissionCount && currentPermissionCount > 0;
}

function UserHeaderCard({ user, companyId }) {
  if (!user) return null;

  const fullName = `${user?.get("first_name")} ${user?.get("last_name")}`;
  const email = user?.get("email");
  const lastLogin = user?.get("last_login");

  return (
    <div className="card shadow-sm mb-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="d-flex align-items-center mb-2">
              <Link
                to={`/company/${companyId}/members`}
                className="btn btn-sm btn-outline-secondary me-3 mr-4"
                title="Back to Members"
              >
                <Octicon name="chevron-left" /> Back to Users
              </Link>
              <h1 className="h3 mb-0 text-primary">{fullName}</h1>
            </div>
            <p className="text-muted mb-2">{email}</p>
            {lastLogin && (
              <p className="text-muted small mb-0">
                <strong>Last login:</strong> {getLastLoginFormatted(lastLogin)}
              </p>
            )}
          </div>
          <div className="text-end">
            <span className="badge badge-pill badge-primary">User Management</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PageDescription() {
  return (
    <div className="mb-4">
      <h2 className="h4 text-dark mb-2">Role Management</h2>
      <p className="text-muted">
        Manage this user's roles and permissions across different dashboards within your organisation. Use the
        checkboxes below to assign or remove roles for specific companies and projects. Changes will be applied when you
        click "Save changes".
      </p>
    </div>
  );
}

function RemoveAllPermissionsModal({ user, companyId, onConfirm, onCancel }) {
  const fullName = `${user?.get("first_name")} ${user?.get("last_name")}`;
  const email = user?.get("email");

  return (
    <Modal show={true} onHide={onCancel} size="md">
      <div className="modal-header">
        <h5 className="modal-title">
          <Octicon name="alert" className="text-warning me-2" />
          Remove All Permissions
        </h5>
      </div>
      <div className="modal-body">
        <div className="alert alert-warning">
          <div className="d-flex">
            <div>
              <h6 className="mb-2">Complete User Removal</h6>
              <p className="mb-2">
                You are about to remove <strong>all permissions</strong> for <strong>{fullName}</strong> ({email}).
              </p>
              <p className="mb-2">
                <strong>This will:</strong>
              </p>
              <ul className="mb-2">
                <li>Remove all their roles and permissions within your organization</li>
                <li>Revoke their access to all projects and dashboards</li>
                <li>Take you back to the user management page</li>
              </ul>
              <p className="mb-0">
                <strong>Are you sure you want to proceed?</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-danger" onClick={onConfirm}>
          <Octicon name="person-x" className="me-2" />
          Yes, Remove All Permissions
        </button>
        <button className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}

export default function CompanyUser() {
  const [user, setUser] = useState(null);
  const [userCompanyRoleNames, setUserCompanyRoleNames] = useState(Im.Set());
  const [userProjectRoleNames, setUserProjectRoleNames] = useState(Im.Set());
  const [userRolesByDashboardId, setUserRolesByDashboardId] = useState(Im.Map());
  let { _companyId, _userId } = useParams();
  const history = useHistory();
  const { setModal, hideModal } = useModal();

  const url = "/api/company/{_companyId}/member/?includeBelowLevel=true";
  const { data: members, trigger, isLoading } = useApi(url, { _companyId });

  const { trigger: triggerThisLevel } = useApi("/api/company/{_companyId}/member/", { _companyId });

  // Nest the dunder-separated raw data from the endpoint
  useEffect(() => {
    if (members) {
      // Filter list of all user roles down to just this user
      const userRoles = members.map((i) => nestByDunderKeys(i)).filter((i) => i.getIn(["user", "pk"]) == _userId);
      const user = userRoles.first()?.get("user");
      setUser(user);

      // If the user is not found, redirect back to members list
      if (!user) {
        console.warn(`User with ID ${_userId} not found in company ${_companyId}. Redirecting to members list.`);
        history.push(`/company/${_companyId}/members`);
        return;
      }

      // Get a unique list of user role names
      setUserCompanyRoleNames(
        userRoles.filter((i) => !!i.get("company")).reduce((acc, m) => acc.add(getRoleName(m)), Im.Set())
      );

      setUserProjectRoleNames(
        userRoles.filter((i) => !!i.get("project")).reduce((acc, m) => acc.add(getRoleName(m)), Im.Set())
      );

      // Get the list of user roles by dashboard (project or company) id
      setUserRolesByDashboardId(
        userRoles.reduce(
          (acc, cur) => acc.update(getDashboardId(cur), (prev) => (prev || Im.List()).push(cur)),
          Im.Map()
        )
      );
    }
  }, [members, _userId]);

  const triggerAllLevels = () => {
    trigger();
    triggerThisLevel();
  };

  // Function to check if saving changes will remove all permissions
  const handleSaveWithPermissionCheck = (updates, originalSaveFunction) => {
    // Check if the updates will result in no permissions
    const willHaveNoPermissions = checkIfUpdatesRemoveAllPermissions(
      updates,
      userRolesByDashboardId,
      userCompanyRoleNames,
      userProjectRoleNames
    );

    if (willHaveNoPermissions) {
      // Show confirmation modal
      setModal(
        <RemoveAllPermissionsModal
          user={user}
          companyId={_companyId}
          onConfirm={() => {
            hideModal();
            // Proceed with the save and redirect after success
            originalSaveFunction().then(() => {
              triggerAllLevels();

              // Small delay to let success message show, then redirect to members list
              const redirectUrl = `/company/${_companyId}/members`;
              console.log("User permissions removed successfully. Redirecting to:", redirectUrl);

              setTimeout(() => {
                history.push(redirectUrl);
              }, 1500); // 1.5 second delay to show success message
            });
          }}
          onCancel={hideModal}
        />
      );
    } else {
      // Normal save operation
      originalSaveFunction().then(() => triggerAllLevels());
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <UserHeaderCard user={user} companyId={_companyId} />

          <div className="card shadow-sm">
            <div className="card-body">
              <PageDescription />

              <Hide when={!isLoading}>
                <div className="d-flex justify-content-center align-items-center py-5">
                  <div className="spinner-border text-primary me-3 mr-3" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <span className="text-muted">Loading user roles...</span>
                </div>
              </Hide>

              <Hide when={isLoading}>
                <UserRoleTree
                  trigger={triggerAllLevels}
                  isLoading={isLoading}
                  userRolesByDashboardId={userRolesByDashboardId}
                  userCompanyRoleNames={userCompanyRoleNames}
                  userProjectRoleNames={userProjectRoleNames}
                  onSaveWithPermissionCheck={handleSaveWithPermissionCheck}
                />
              </Hide>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
